import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { useSnackbar } from 'notistack';

import { printDocumentInHiddenFrame } from '~/pages/LeLens/customView/customLensPrint';
import { createLensBoardPrintDocument } from '~/pages/LeLens/lensPdfExport';
import type { LensPrintTarget } from '~/pages/LeLens/print/lensPrintTypes';

export function useNativeLensPrint({
  root,
  ready,
  error,
  target,
  title,
  expectedCardCount,
  includeHeading = true,
}: {
  root: RefObject<HTMLElement | null>;
  ready: boolean;
  error?: string | null;
  target?: LensPrintTarget;
  title: string;
  expectedCardCount: number;
  includeHeading?: boolean;
}) {
  const started = useRef(false);
  const [downloading, setDownloading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if ((!target && !downloading) || started.current) return;
    if (error) {
      started.current = true;
      target?.reject(new Error(error));
      if (downloading) {
        enqueueSnackbar(error, { variant: 'error' });
        setDownloading(false);
        started.current = false;
      }
      return;
    }
    if (!ready || !root.current) return;
    started.current = true;
    const element = root.current;
    void (async () => {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await new Promise((resolve) => setTimeout(resolve, 800));
      const html = await createLensBoardPrintDocument({
        root: element,
        title,
        expectedCardCount,
        includeHeading,
        generatedAt: target?.generatedAt,
      });
      if (target) target.resolve({ kind: 'html', html, warnings: [] });
      else await printDocumentInHiddenFrame(html);
    })()
      .catch((failure) => {
        const problem = failure instanceof Error ? failure : new Error(String(failure));
        target?.reject(problem);
        if (!target) enqueueSnackbar(problem.message, { variant: 'error' });
      })
      .finally(() => {
        if (!target) {
          setDownloading(false);
          started.current = false;
        }
      });
  }, [root, ready, error, target, title, expectedCardCount, includeHeading, downloading, enqueueSnackbar]);
  return { printing: Boolean(target || downloading), downloadPdf: () => setDownloading(true) };
}
